/**
 * HDN · Catálogo: barra «Ver como · Items por página · Ordenar por» (<hdn-barra-catalogo>) y
 * «Ver más» (<hdn-ver-mas>), portados del tema anterior (Ella, assets/ella.min.js:
 * addEventViewModeLayout, viewModeLayout, setActiveViewModeMediaQuery, ajaxFilterSortby,
 * doAjaxToolbarGetContent, initInfiniteScrolling y ajaxInfiniteScrollingMapData), sin jQuery.
 * El marcado está en snippets/hdn-barra-catalogo.liquid y snippets/hdn-ver-mas.liquid.
 *
 * Igual que en el anterior:
 * - «Ver como» cambia la vista al momento y no se recuerda al recargar. Al cargar, si la vista no
 *   cabe en el ancho, se elige la mayor que cabe (aquí también al girar o estrechar la ventana).
 * - «Ordenar por» trae la colección en el orden elegido sin recargar, cambia la dirección
 *   (sort_by, quitando page) y conserva la vista.
 * - «Ver más» añade los siguientes productos sin recargar.
 *
 * Distinto, por decisión del usuario y del director (docs/ordenes/2026-10-14-slider-y-catalogo.md):
 * - Items por página en localStorage (antes, un atributo del carrito que acababa en los pedidos) y
 *   sin recargar: Liquid pinta 50 por página y el CSS enseña los N elegidos (data-hdn-por-pagina en
 *   el contenedor). «Ver más» marca los N siguientes con data-hdn-visible y, cuando se acaban, trae
 *   la página siguiente por la API de secciones, conservando sort_by.
 * - «Ver más»: el foco pasa al primer producto nuevo y un aviso (aria-live) dice cuántos se han
 *   añadido. Al llegar al final se queda gris, «No hay más productos», sin poder pulsarse (como el
 *   anterior). Sin carga automática al bajar (el anterior la tenía puesta pero no funcionaba).
 * - Las vistas son un grupo de radio que también se maneja con las flechas del teclado.
 */

const CLAVE = 'hdn-por-pagina';
const OPCIONES = [12, 16, 20, 24, 30, 50];
const POR_DEFECTO = 24;

const contenedorDe = (elemento) => elemento.closest('results-list');
const rejillaDe = (contenedor) => contenedor?.querySelector('[ref="grid"]') ?? null;
const productos = (rejilla) => (rejilla ? [...rejilla.querySelectorAll(':scope > .product-grid__item')] : []);
const visible = (elemento) => elemento.getClientRects().length > 0;

/** La mayor vista que cabe en este ancho (el anterior: 2 en móvil, 3 en tableta, 4 hasta 1200). */
function vistaMaxima() {
  const ancho = window.innerWidth;
  if (ancho < 750) return 2;
  if (ancho < 990) return 3;
  if (ancho < 1200) return 4;
  return 5;
}

function leerPorPagina() {
  try {
    const guardado = Number(localStorage.getItem(CLAVE));
    return OPCIONES.includes(guardado) ? guardado : POR_DEFECTO;
  } catch {
    return POR_DEFECTO;
  }
}

/** Pide una dirección del catálogo por la API de secciones y devuelve su rejilla (o null). */
async function pedirRejilla(direccion, seccion, signal) {
  const url = new URL(direccion, window.location.origin);
  url.searchParams.set('section_id', seccion);
  url.hash = '';
  const respuesta = await fetch(url, { signal });
  if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
  return rejillaDeHtml(await respuesta.text());
}

function rejillaDeHtml(html) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');
}

class HdnBarraCatalogo extends HTMLElement {
  connectedCallback() {
    this.contenedor = contenedorDe(this);
    if (!this.contenedor) return;
    this.iconos = [...this.querySelectorAll('[data-col]')];
    this.porPagina = this.querySelector('[data-por-pagina]');
    this.orden = this.querySelector('[data-orden]');
    this.aviso = this.querySelector('[data-aviso]');
    this.agrandadas = new WeakMap();

    // En el editor de temas la sección se repinta sin ejecutar el script en línea: se pone aquí.
    if (!this.contenedor.dataset.hdnVista) this.estadoInicial();

    this.alPulsar = (event) => {
      const icono = event.target.closest('[data-col]');
      if (icono) this.elegirVista(icono);
    };
    this.alTeclear = (event) => this.moverConFlechas(event);
    this.alCambiarPorPagina = () => this.cambiarPorPagina();
    this.alCambiarOrden = () => this.ordenar(this.orden.value);
    this.alVolver = () => this.volver();
    this.alCambiarAncho = () => this.ajustarAlAncho();

    this.addEventListener('click', this.alPulsar);
    this.addEventListener('keydown', this.alTeclear);
    this.porPagina?.addEventListener('change', this.alCambiarPorPagina);
    this.orden?.addEventListener('change', this.alCambiarOrden);
    window.addEventListener('popstate', this.alVolver);
    this.anchos = ['(min-width: 750px)', '(min-width: 990px)', '(min-width: 1200px)'].map((consulta) => window.matchMedia(consulta));
    for (const ancho of this.anchos) ancho.addEventListener('change', this.alCambiarAncho);

    // Horizon devuelve los tamaños de las fotos a los suyos cuando cambia la rejilla; se reponen.
    this.vigilante = new MutationObserver(() => requestAnimationFrame(() => this.reponerFotos()));
    const rejilla = rejillaDe(this.contenedor);
    if (rejilla) this.vigilante.observe(rejilla, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.alPulsar);
    this.removeEventListener('keydown', this.alTeclear);
    this.porPagina?.removeEventListener('change', this.alCambiarPorPagina);
    this.orden?.removeEventListener('change', this.alCambiarOrden);
    window.removeEventListener('popstate', this.alVolver);
    for (const ancho of this.anchos ?? []) ancho.removeEventListener('change', this.alCambiarAncho);
    this.vigilante?.disconnect();
    this.peticion?.abort();
  }

  get vista() {
    return this.contenedor.dataset.hdnVista || '4';
  }

  /** Lo mismo que el script en línea de la barra. */
  estadoInicial() {
    const vista = Math.min(4, vistaMaxima());
    for (const icono of this.iconos) this.marcar(icono, Number(icono.dataset.col) === vista);
    this.contenedor.dataset.hdnVista = String(vista);
    if (this.porPagina) {
      const n = leerPorPagina();
      this.porPagina.value = String(n);
      this.contenedor.dataset.hdnPorPagina = String(n);
    }
  }

  marcar(icono, elegido) {
    icono.setAttribute('aria-checked', String(elegido));
    icono.tabIndex = elegido ? 0 : -1;
  }

  elegirVista(icono) {
    if (icono.getAttribute('aria-checked') === 'true') return;
    for (const otro of this.iconos) this.marcar(otro, otro === icono);
    this.contenedor.dataset.hdnVista = icono.dataset.col === '1' ? 'lista' : icono.dataset.col;
    this.ajustarFotos();
  }

  /** Si la vista elegida ya no cabe (se ha estrechado la ventana), la mayor que cabe. */
  ajustarAlAncho() {
    const vista = this.vista;
    if (vista === 'lista') return;
    const maxima = vistaMaxima();
    if (Number(vista) <= maxima) return;
    const icono = this.iconos.find((otro) => Number(otro.dataset.col) === maxima);
    if (icono) this.elegirVista(icono);
  }

  /** Grupo de radio (WAI-ARIA): las flechas mueven la elección entre las vistas visibles. */
  moverConFlechas(event) {
    const icono = event.target.closest('[data-col]');
    if (!icono) return;
    const visibles = this.iconos.filter(visible);
    const posicion = visibles.indexOf(icono);
    let destino;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        destino = visibles[(posicion + 1) % visibles.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        destino = visibles[(posicion - 1 + visibles.length) % visibles.length];
        break;
      case 'Home':
        destino = visibles[0];
        break;
      case 'End':
        destino = visibles[visibles.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    destino.focus();
    this.elegirVista(destino);
  }

  /**
   * Al agrandar la vista (2 o 3 columnas, o la lista en el móvil) las fotos ya cargadas se verían
   * borrosas: a esas se les da su ancho real (sizes) y el navegador pide la que toca. Las que aún
   * no han cargado llevan sizes="auto" (Horizon) y cogerán la buena al cargar. Al cargar la página
   * y al achicar no se toca nada, para no descargar dos veces la misma foto.
   */
  ajustarFotos() {
    const rejilla = rejillaDe(this.contenedor);
    if (!rejilla) return;
    const densidad = window.devicePixelRatio || 1;
    for (const foto of rejilla.querySelectorAll('.card-gallery img')) {
      const ancho = foto.getBoundingClientRect().width;
      // Oculta, o ya agrandada a este ancho o más: nada que hacer.
      if (!ancho || (parseFloat(this.agrandadas.get(foto)) || 0) >= ancho) continue;
      const cargada = foto.complete && foto.currentSrc;
      if (!cargada && (foto.getAttribute('sizes') || '').startsWith('auto')) continue;
      const pedida = cargada ? Number(new URL(foto.currentSrc, window.location.href).searchParams.get('width')) || 0 : 0;
      if (pedida && pedida >= ancho * densidad) continue;
      const tamano = `${Math.ceil(ancho)}px`;
      this.agrandadas.set(foto, tamano);
      foto.setAttribute('sizes', tamano);
    }
  }

  reponerFotos() {
    const rejilla = rejillaDe(this.contenedor);
    if (!rejilla) return;
    for (const foto of rejilla.querySelectorAll('.card-gallery img')) {
      const tamano = this.agrandadas.get(foto);
      if (tamano && foto.getAttribute('sizes') !== tamano) foto.setAttribute('sizes', tamano);
    }
  }

  cambiarPorPagina() {
    const n = Number(this.porPagina.value);
    if (!OPCIONES.includes(n)) return;
    try {
      localStorage.setItem(CLAVE, String(n));
    } catch {
      // Sin almacenamiento (navegación privada estricta): vale para esta página.
    }
    this.contenedor.dataset.hdnPorPagina = String(n);
    this.contenedor.dispatchEvent(new CustomEvent('hdn:catalogo', { detail: { motivo: 'por-pagina' } }));
  }

  async ordenar(valor) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', valor);
    url.searchParams.delete('page');
    url.hash = '';
    history.pushState({ hdnOrden: valor }, '', url);
    await this.cargar(url);
  }

  /** Atrás y adelante después de ordenar: se pinta el orden que diga la dirección. */
  volver() {
    if (!this.orden) return;
    const valor = new URL(window.location.href).searchParams.get('sort_by') || this.orden.dataset.porDefecto;
    if (!valor || valor === this.orden.value) return;
    this.orden.value = valor;
    this.cargar(new URL(window.location.href));
  }

  async cargar(url) {
    const rejilla = rejillaDe(this.contenedor);
    const seccion = this.contenedor.getAttribute('section-id');
    if (!rejilla || !seccion) {
      window.location.assign(url);
      return;
    }
    this.peticion?.abort();
    this.peticion = new AbortController();
    this.contenedor.setAttribute('aria-busy', 'true');
    try {
      const nueva = await pedirRejilla(url, seccion, this.peticion.signal);
      if (!nueva) throw new Error('Sin rejilla');
      rejilla.replaceChildren(...productos(nueva));
      rejilla.dataset.lastPage = nueva.dataset.lastPage ?? '1';
      rejilla.dataset.hdnPagina = nueva.dataset.hdnPagina ?? '1';
      this.contenedor.dispatchEvent(new CustomEvent('hdn:catalogo', { detail: { motivo: 'orden' } }));
      const nombre = this.orden?.selectedOptions[0]?.textContent.trim() ?? '';
      if (this.aviso) this.aviso.textContent = (this.dataset.textoOrdenado || '').replace('[orden]', nombre);
    } catch (error) {
      // Si falla, como siempre: la página con ese orden.
      if (error?.name !== 'AbortError') window.location.assign(url);
    } finally {
      this.contenedor.removeAttribute('aria-busy');
    }
  }
}

class HdnVerMas extends HTMLElement {
  connectedCallback() {
    this.contenedor = contenedorDe(this);
    this.rejilla = rejillaDe(this.contenedor);
    this.aviso = this.querySelector('[data-aviso]');
    if (!this.contenedor || !this.rejilla) return;
    this.seccion = this.contenedor.getAttribute('section-id');

    // Sin JavaScript es un enlace a la página siguiente; con él, un botón que añade sin recargar.
    const enlace = this.querySelector('a');
    if (enlace) {
      const boton = document.createElement('button');
      boton.type = 'button';
      boton.className = enlace.className;
      boton.textContent = enlace.textContent;
      enlace.replaceWith(boton);
    }
    this.boton = this.querySelector('button');

    this.alPulsar = (event) => {
      event.preventDefault();
      this.verMas();
    };
    this.alCambiar = (event) => this.reiniciar(event.detail?.motivo);
    this.boton?.addEventListener('click', this.alPulsar);
    this.contenedor.addEventListener('hdn:catalogo', this.alCambiar);

    this.reiniciar();
    // Desde aquí manda el JavaScript (hidden), no el CSS del primer pintado.
    this.dataset.hdnListo = '';
  }

  disconnectedCallback() {
    this.boton?.removeEventListener('click', this.alPulsar);
    this.contenedor?.removeEventListener('hdn:catalogo', this.alCambiar);
    this.peticion?.abort();
  }

  /** Productos por página elegidos; 0 si la barra no tiene «Items por página» (se ve todo). */
  get porPagina() {
    const n = Number(this.contenedor.dataset.hdnPorPagina);
    return OPCIONES.includes(n) ? n : 0;
  }

  get haySiguiente() {
    return this.pagina < this.ultima;
  }

  reiniciar(motivo) {
    this.peticion?.abort();
    this.peticion = new AbortController();
    this.paginas = new Map();
    // Las páginas que Horizon deja precargadas valen mientras no cambie el orden.
    if (motivo === 'orden') this.sinPrecargaHorizon = true;
    this.agotado = false;
    this.pagina = Number(this.rejilla.dataset.hdnPagina) || 1;
    this.ultima = Number(this.rejilla.dataset.lastPage) || 1;
    for (const producto of this.rejilla.querySelectorAll(':scope > [data-hdn-visible]')) {
      producto.removeAttribute('data-hdn-visible');
    }
    this.visibles = this.porPagina || Infinity;
    this.actualizar();
    this.precargar();
  }

  /**
   * Con algo más que enseñar, el botón normal. Sin nada más: oculto si no se ha llegado pulsando (una
   * colección corta o un número por página mayor que lo que hay) y, si se ha llegado pulsando, gris
   * y sin poder pulsarse, «No hay más productos», como el anterior.
   */
  actualizar(trasPulsar = false) {
    if (!this.boton) return;
    const quedan = productos(this.rejilla).length > this.visibles || this.haySiguiente;
    if (!quedan && trasPulsar) this.agotado = true;
    this.hidden = !quedan && !this.agotado;
    if (quedan) {
      this.boton.removeAttribute('aria-disabled');
      this.boton.textContent = this.dataset.textoVerMas || this.boton.textContent;
    } else if (this.agotado) {
      this.boton.setAttribute('aria-disabled', 'true');
      this.boton.textContent = this.dataset.textoNoHayMas || this.boton.textContent;
    }
  }

  async verMas() {
    if (!this.boton || this.boton.getAttribute('aria-busy') === 'true' || this.boton.getAttribute('aria-disabled') === 'true') return;
    const n = this.porPagina;
    const desde = Math.min(this.visibles, productos(this.rejilla).length);
    const objetivo = n ? desde + n : Infinity;

    this.boton.setAttribute('aria-busy', 'true');
    try {
      if (n) {
        while (productos(this.rejilla).length < objetivo && this.haySiguiente) await this.traerSiguiente();
      } else if (this.haySiguiente) {
        await this.traerSiguiente();
      }
    } catch (error) {
      if (error?.name !== 'AbortError' && this.aviso) this.aviso.textContent = this.dataset.textoError || '';
    } finally {
      this.boton.removeAttribute('aria-busy');
    }

    const todos = productos(this.rejilla);
    const nuevos = todos.slice(desde, Number.isFinite(objetivo) ? objetivo : todos.length);
    for (const producto of nuevos) producto.setAttribute('data-hdn-visible', '');
    this.visibles = n ? Math.min(objetivo, todos.length) : Infinity;
    this.actualizar(true);

    if (nuevos.length) {
      nuevos[0].querySelector('a[href]')?.focus({ preventScroll: true });
      if (this.aviso) {
        this.aviso.textContent =
          nuevos.length === 1 ? this.dataset.textoUno || '' : (this.dataset.textoAnadidos || '').replace('[n]', String(nuevos.length));
      }
    }
    this.precargar();
  }

  async traerSiguiente() {
    const pagina = this.pagina + 1;
    const nuevos = await this.descargar(pagina);
    const yaEstan = new Set(productos(this.rejilla).map((producto) => producto.dataset.productId));
    this.rejilla.append(...nuevos.filter((producto) => !yaEstan.has(producto.dataset.productId)));
    this.pagina = pagina;
  }

  /** Los productos de una página (de 50), pedidos una sola vez. */
  descargar(pagina) {
    if (!this.paginas.has(pagina)) {
      const precargada = this.sinPrecargaHorizon ? null : this.contenedor.pages?.get?.(pagina);
      let promesa;
      if (typeof precargada === 'string') {
        promesa = Promise.resolve(rejillaDeHtml(precargada));
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(pagina));
        promesa = pedirRejilla(url, this.seccion, this.peticion.signal);
      }
      promesa = promesa.then((rejilla) => {
        if (!rejilla) throw new Error('Sin rejilla');
        this.ultima = Number(rejilla.dataset.lastPage) || this.ultima;
        return productos(rejilla);
      });
      promesa.catch(() => this.paginas.delete(pagina));
      this.paginas.set(pagina, promesa);
    }
    return this.paginas.get(pagina);
  }

  /** Si al próximo «Ver más» le van a faltar productos, la página siguiente se pide ya, en reposo. */
  precargar() {
    const n = this.porPagina;
    if (!n || !this.haySiguiente) return;
    if (productos(this.rejilla).length - this.visibles >= n) return;
    const pedir = () => this.descargar(this.pagina + 1).catch(() => {});
    if ('requestIdleCallback' in window) window.requestIdleCallback(pedir, { timeout: 3000 });
    else window.setTimeout(pedir, 300);
  }
}

if (!customElements.get('hdn-barra-catalogo')) customElements.define('hdn-barra-catalogo', HdnBarraCatalogo);
if (!customElements.get('hdn-ver-mas')) customElements.define('hdn-ver-mas', HdnVerMas);
